import { possessive } from '@shared';
import { DialogModal } from '@/components/DialogModal';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { useClientDuplication } from '@/hooks/useClientDuplication';

interface ClientDuplicationDialogProps {
  client: Client;
  open: boolean;
  onClose: () => void;
}

export function ClientDuplicationDialog({ client, open, onClose }: ClientDuplicationDialogProps) {
  const { isLoading, copyRelatedData, setCopyRelatedData, handleClientDuplication, handleOnClose } =
    useClientDuplication({ open, client, onClose });

  return (
    <DialogModal
      open={open}
      onClose={handleOnClose}
      title="Duplicate Client"
      subTitle={`Create a copy of ${client.clientFirstName} ${possessive(client.clientLastName)} data.`}
      isLoading={isLoading}
      primaryButton={
        <Button
          onClick={handleClientDuplication}
          disabled={isLoading}
          className="bg-blue-600 hover:bg-blue-700"
        >
          {isLoading ? 'Duplicating...' : 'Duplicate Client'}
        </Button>
      }
    >
      <div className="space-y-4">
        <div className="space-y-4">
          <div className="text-sm text-gray-700">
            <p>
              This will create a new client record with the same advisor assignment, referral
              source, and intake form data as the original.
            </p>
          </div>
          <div className="space-y-2">
            <div className="flex items-center space-x-2">
              <Checkbox
                id="copyRelatedData"
                checked={copyRelatedData}
                onCheckedChange={checked => setCopyRelatedData(checked as boolean)}
              />
              <Label
                htmlFor="copyRelatedData"
                className="text-sm font-medium text-gray-700"
              >
                Copy related client data (care environment selections, funding sources, etc.)
              </Label>
            </div>
            <div className="ml-6 text-xs text-gray-700">
              <p>
                When checked: Duplicates all care environment selections, funding sources, and other
                related data.
                <br />
                When unchecked: Creates a fresh client with only the intake form data.
              </p>
            </div>
          </div>
        </div>
      </div>
    </DialogModal>
  );
}
